import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { enumLabel } from "@/i18n/enumLabels";
import {
  SUPPLIER_TYPE_LABELS,
  SUPPLIER_PAYMENT_PROFILE_LABELS,
  type SupplierType,
  type SupplierPaymentProfile,
  SUPPLIER_TYPES,
  SUPPLIER_PAYMENT_PROFILES,
} from "@ckgo2/shared/types";
import { formatDate } from "@/lib/format";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { Plus, Building2, Package } from "lucide-react";

interface SupplierRow {
  id: string;
  name: string;
  type: SupplierType;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  currency: string;
  paymentProfile: SupplierPaymentProfile;
  paymentTerms?: number | null;
  destinationId?: string | null;
  isActive: boolean;
  createdAt: string;
  destinationName?: string | null;
}

interface FacetedSuppliers {
  data: SupplierRow[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

export default function SuppliersPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["suppliers", qs],
    queryFn: () => api.get<FacetedSuppliers>(`/api/admin/suppliers?${qs}`),
  });

  const { data: destData } = useAdminDestinationList();
  const destName = (id: string) => destData?.data.find((d) => d.id === id)?.name ?? id;

  const suppliers = data?.data ?? [];
  const facets = data?.facets ?? {};
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("opsAdmin.suppliers.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("opsAdmin.suppliers.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/suppliers/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            {t("opsAdmin.suppliers.new")}
          </Link>
          <Link to="/supplier-components" className="btn-ghost">
            <Package className="h-5 w-5" />
            {t("opsAdmin.suppliers.pricedCatalog")}
          </Link>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("opsAdmin.suppliers.searchPlaceholder")}
        total={total}
        limit={limit}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title={t("common.fields.type")}
              paramKey="type"
              order={{ enum: SUPPLIER_TYPES }}
              buckets={facets.type ?? []}
              labelFor={(v) => enumLabel(SUPPLIER_TYPE_LABELS, v as SupplierType)}
            />
            <FacetPanel
              title={t("opsAdmin.common.payment")}
              paramKey="paymentProfile"
              order={{ enum: SUPPLIER_PAYMENT_PROFILES }}
              buckets={facets.paymentProfile ?? []}
              labelFor={(v) =>
                enumLabel(SUPPLIER_PAYMENT_PROFILE_LABELS, v as SupplierPaymentProfile)
              }
            />
            <FacetPanel
              title={t("common.fields.destination")}
              paramKey="destinationId"
              order="name"
              buckets={facets.destinationId ?? []}
              labelFor={destName}
              nullLabel={t("opsAdmin.common.global")}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {suppliers.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <Building2 className="mb-2 h-10 w-10 text-gray-300" />
              <p>{t("opsAdmin.suppliers.empty")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {[
                    t("opsAdmin.suppliers.colSupplier"),
                    t("common.fields.type"),
                    t("opsAdmin.suppliers.colContact"),
                    t("common.fields.currency"),
                    t("opsAdmin.common.payment"),
                    t("common.fields.destination"),
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {suppliers.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/suppliers/${s.id}`}
                        className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                      >
                        {s.name}
                      </Link>
                      {!s.isActive && (
                        <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                          {t("common.status.inactive")}
                        </span>
                      )}
                      <p className="text-xs text-gray-400">{formatDate(s.createdAt)}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {enumLabel(SUPPLIER_TYPE_LABELS, s.type)}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {s.contactName || "—"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {s.contactEmail || s.contactPhone || ""}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {s.currency}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {enumLabel(SUPPLIER_PAYMENT_PROFILE_LABELS, s.paymentProfile)}
                      {s.paymentTerms != null && (
                        <span className="text-gray-400">
                          {" "}
                          · {t("opsAdmin.suppliers.paymentTermsDays", { count: s.paymentTerms })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {s.destinationName || (
                        <span className="text-gray-400">{t("opsAdmin.common.global")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
